import Image from 'next/image';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import DigitalCardLayerStyled from 'src/components/_common/DigitalCard/_components/DigitalCardLayer/index.styled';
import type { ImageLayer } from 'src/components/_common/DigitalCard/_components/DigitalCardLayer/types';
import {
  useDigitalCardActions,
  useDigitalCardConfig,
  useDigitalCardState,
} from 'src/components/_common/DigitalCard/_hooks/useDigitalCard';
import { useImageLoad } from 'src/components/_common/DigitalCard/_hooks/useImageLoad';
import styled from 'styled-components';

interface ImageLayerProps extends ImageLayer {
  faceIdx: number;
}

/**
 * [디지털 카드 이미지 레이어 컴포넌트]
 *
 * @description
 * 이미지 레이어를 디지털 카드에 표시하는 컴포넌트입니다.
 * 이미지 로딩 상태를 관리하고, 이미지 로딩 완료 시 이미지 로딩 완료 핸들러를 호출합니다.
 *
 * @param {ImageLayer} props - 이미지 레이어 컴포넌트의 속성
 * ```ts
 * {
 *   idx: number; // 레이어의 고유 식별자
 *   name: string; // 레이어의 이름
 *   type: 'image'; // 레이어 타입
 *   zIndex: number; // 레이어의 z-index 값
 *   translateZ: number; // 레이어의 z축 변환 값
 *   borderRadius: number; // 레이어의 모서리 둥글기
 *   imgSrc: string; // 이미지 소스 URL
 *   width: number | undefined; // 레이어 너비 (지정 시 전체 너비 대신 해당 px 사용, 카드 크기에 비례해 스케일링됨)
 *   height: number | undefined; // 레이어 높이 (지정 시 전체 높이 대신 해당 px 사용, 카드 크기에 비례해 스케일링됨)
 *   top: number | undefined; // 레이어 top 위치 (지정 시 0 대신 해당 px 사용, 카드 크기에 비례해 스케일링됨)
 *   left: number | undefined; // 레이어 left 위치 (지정 시 0 대신 해당 px 사용, 카드 크기에 비례해 스케일링됨)
 *   faceIdx: number; // 디지털 카드의 면 인덱스
 * }
 * ```
 */
export const DigitalCardImageLayer = ({
  name,
  zIndex,
  translateZ,
  borderRadius,
  imgSrc,
  width,
  height,
  top,
  left,
}: ImageLayerProps) => {
  const { imageLoadRetryCount, imageLoadDelayTime, width: designWidth } = useDigitalCardConfig();
  const { cardRef } = useDigitalCardState();
  const { handleResourceLoadComplete, handleResourceLoadError } = useDigitalCardActions();

  const imageRef = useRef<HTMLImageElement>(null);
  const [scale, setScale] = useState(1);

  const onLoadComplete = useCallback(() => {
    handleResourceLoadComplete(imgSrc);
  }, [handleResourceLoadComplete, imgSrc]);

  const onLoadError = useCallback(() => {
    handleResourceLoadError();
  }, [handleResourceLoadError]);

  useImageLoad({
    imageRef,
    useImageObject: false,
    src: imgSrc,
    onLoadComplete,
    onLoadError,
    retryCount: imageLoadRetryCount,
    delayTime: imageLoadDelayTime,
  });

  const hasPositioning = width !== undefined || height !== undefined || top !== undefined || left !== undefined;

  useEffect(() => {
    if (!hasPositioning) return;
    const calculateScale = () => {
      if (cardRef?.current?.clientWidth) {
        setScale(cardRef.current.clientWidth / designWidth);
      }
    };
    calculateScale();
    const resizeObserver = new ResizeObserver(calculateScale);
    if (cardRef?.current) {
      resizeObserver.observe(cardRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [cardRef, designWidth, hasPositioning]);

  const scaled = (value: number | undefined) => (value !== undefined ? scale * value : undefined);

  return (
    <ImageLayerWrapper
      $zIndex={zIndex}
      $translateZ={translateZ}
      $borderRadius={borderRadius}
      $width={scaled(width)}
      $height={scaled(height)}
      $top={scaled(top)}
      $left={scaled(left)}
    >
      <Image src={imgSrc} alt={`[카드 레이어] ${name}`} ref={imageRef} unoptimized={true} fill />
    </ImageLayerWrapper>
  );
};

interface ImageLayerWrapperProps {
  $width?: number;
  $height?: number;
  $top?: number;
  $left?: number;
}

const ImageLayerWrapper = styled(DigitalCardLayerStyled.BaseLayerWrapper)<ImageLayerWrapperProps>`
  overflow: ${({ $borderRadius }) => (!$borderRadius ? 'visible' : 'hidden')};
  ${({ $borderRadius }) => ($borderRadius ? `border-radius: ${$borderRadius}px;` : '')}
  ${({ $width }) => ($width !== undefined ? `width: ${$width}px;` : '')}
  ${({ $height }) => ($height !== undefined ? `height: ${$height}px;` : '')}
  ${({ $top }) => ($top !== undefined ? `top: ${$top}px;` : '')}
  ${({ $left }) => ($left !== undefined ? `left: ${$left}px;` : '')}
`;
